import { createContext, useContext } from "react";
import {
  type StatementItemReference,
  getSectionItemReferences,
} from "./statement-item-reference";
import type { SectionBlock } from "./statement-schema";

export const STATEMENT_ITEM_MARK = {
  open: "open",
  closed: "closed",
} as const;

export type StatementItemMark =
  (typeof STATEMENT_ITEM_MARK)[keyof typeof STATEMENT_ITEM_MARK];

export const STATEMENT_ITEM_ACTION_INTENT = {
  askQuestion: "ask-question",
  startThread: "start-thread",
} as const;

export type StatementItemActionIntent =
  (typeof STATEMENT_ITEM_ACTION_INTENT)[keyof typeof STATEMENT_ITEM_ACTION_INTENT];

export interface StatementItemAction {
  onAction: (reference: StatementItemReference) => void;
  intent?: StatementItemActionIntent;
  activeKey?: string | null;
  marks?: Record<string, StatementItemMark>;
  activeRowKey?: string | null;
  marksOnly?: boolean;
}

export const ItemActionContext = createContext<StatementItemAction | null>(
  null,
);

export const ACTIVE_TEXT =
  "-mx-1 box-decoration-clone rounded bg-warning-subtle px-1";

export const sectionRowKeys = (block: SectionBlock) =>
  getSectionItemReferences(block).map(statementRowKey);

export const useActiveRow = (reference: StatementItemReference) => {
  const action = useContext(ItemActionContext);
  const active = action?.activeRowKey;
  return Boolean(active) && active === statementRowKey(reference);
};

export const referenceKey = (reference: StatementItemReference): string =>
  [reference.heading ?? "", reference.line ?? "", reference.amount ?? ""].join(
    "|",
  );

export const statementRowKey = (reference: StatementItemReference): string =>
  [reference.heading, reference.line]
    .map((part) => (part ?? "").toLowerCase().replace(/\s+/g, " ").trim())
    .join("|");
